import type { ComponentDocs } from '../component-docs.js';
import { SIZE_STEPS } from '../sizes.js';

/**
 * What `<caos-avatar>` says about itself.
 *
 * ── THE FALLBACK IS THE POINT ────────────────────────────────────────────────
 * Picture, else initials, else the `user` glyph. The examples show each rung, including a picture
 * that fails to load, because that is the case people otherwise solve again on every screen.
 *
 * ── SIZES ARE THE PLATFORM SCALE'S ───────────────────────────────────────────
 * The diameter reads `--caos-size-<step>-height` (CAOS-586, CAOS-594). The avatar has no size and no
 * token named after it.
 */

/** A small picture carried inline, so the example needs nothing fetched from anywhere. */
const PICTURE =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 40 40'%3E" +
  "%3Crect width='40' height='40' fill='%23577590'/%3E%3Ccircle cx='20' cy='16' r='7' fill='%23f1faee'/%3E" +
  "%3Crect x='8' y='26' width='24' height='16' rx='8' fill='%23f1faee'/%3E%3C/svg%3E";

const docs: ComponentDocs = {
  aliases: ['profile picture', 'user picture', 'initials', 'person picture', 'owner picture', 'headshot'],

  answers: [
    'show who owns a record',
    "show a person's initials when there is no picture",
    'draw a user as a circle beside their name',
    'make a profile picture bigger in a record header',
  ],

  placements: ['recordPage', 'homePage', 'appPage'],

  inputs: [
    {
      name: 'name',
      attribute: 'name',
      type: 'text',
      description:
        "The person the avatar stands for. Its initials are drawn when there is no picture, and it is the " +
        "avatar's name for a screen reader.",
    },
    {
      name: 'src',
      attribute: 'src',
      type: 'text',
      description:
        'The address of the picture. When it is missing or fails to load, the initials are drawn instead, ' +
        'and a picture that failed is not tried again until this changes.',
    },
    {
      name: 'alt',
      attribute: 'alt',
      type: 'text',
      description: 'What a screen reader says in place of the name, when the name alone is not the right description.',
    },
    {
      name: 'size',
      attribute: 'size',
      type: 'text',
      options: [...SIZE_STEPS],
      description: 'The diameter, from the platform size scale: xs, sm, md, lg or xl. The initials scale with it.',
      default: 'md',
    },
  ],

  events: [],

  state: [],

  slots: [],

  tokens: [
    ...SIZE_STEPS.map((step) => ({
      token: `--caos-size-${step}-height`,
      controls: `The diameter at size ${step}.`,
    })),
    { token: '--caos-color-surface-2', controls: 'The fill behind the initials and the glyph.' },
    { token: '--caos-color-border', controls: 'The 1px edge of the circle.' },
    { token: '--caos-color-text-muted', controls: 'The initials and the glyph.' },
    { token: '--caos-radius-pill', controls: 'The circle.' },
    { token: '--caos-font-mono', controls: 'The typeface of the initials.' },
    { token: '--caos-font-weight-label', controls: 'The weight of the initials.' },
  ],

  parts: [
    { part: 'avatar', is: 'The circle.' },
    { part: 'image', is: 'The picture, when one is drawn.' },
    { part: 'initials', is: 'The initials, when they are drawn.' },
  ],

  methods: [],

  placing:
    'Inline, beside the name it belongs to: at the start of an owner field, in a row of a people list, ' +
    'in a record header. It is inline-flex and vertically centred, adds no spacing of its own, and never ' +
    'wraps. Take the step that matches what sits beside it, such as xs beside dense row text, md beside a ' +
    'field value and xl in a record header.',

  whenToUse:
    'The thing shown is a person, and a small recognisable circle helps someone find them in a list or ' +
    'confirm who they are looking at.',

  insteadUse: [
    { name: 'Icon', when: 'The mark stands for a kind of thing rather than a particular person.' },
    { name: 'Tag', when: 'What needs showing is a word about the record, not who it belongs to.' },
  ],

  accessibility:
    'With a name or alt text the avatar is one image with that name, and the picture and initials inside ' +
    'it are hidden, so a screen reader hears the name once rather than two letters. With neither it is ' +
    'decorative and hidden entirely. Initials help someone recognise a person; they never replace the ' +
    'name written beside the avatar.',

  examples: [
    {
      key: 'initials',
      label: 'Initials',
      summary: 'A person with no picture, drawn as the first letters of their first and last names.',
      placement: { component: 'caos-avatar', inputs: { name: 'Morgan Lee' } },
    },
    {
      key: 'picture',
      label: 'A picture',
      summary: 'A person with a picture, cropped to the circle.',
      placement: { component: 'caos-avatar', inputs: { name: 'Alex Chen', src: PICTURE } },
    },
    {
      key: 'sizes',
      label: 'The size scale',
      summary: 'The same person at every step of the platform size scale, from xs to xl.',
      placement: SIZE_STEPS.map((size) => ({ component: 'caos-avatar', inputs: { name: 'Morgan Lee', size } })),
    },
    {
      key: 'no-name',
      label: 'Nobody named',
      summary: 'No picture and no name: the user glyph from the platform vocabulary, and nothing for a screen reader.',
      placement: { component: 'caos-avatar' },
    },
  ],
};

export default docs;
